import React, { useState, useEffect } from "react";
import { Lock, EyeOff, Eye, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { resetPassword } from "../ApiHelper/url_Helpers";
import { RiseLoader } from "react-spinners";
import { toasterSuccess, toasterError } from "./toaster";
import "./loader.css";

export const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const email = sessionStorage.getItem("resetEmail");
  const otpVerified = sessionStorage.getItem("otpVerified");

  useEffect(() => {
    if (!email || !otpVerified) {
      navigate("/login");
    }
  }, [email, otpVerified, navigate]);

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validatePassword(newPassword)) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      let payload = {
        email,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }
      let result = await resetPassword(payload);
      toasterSuccess("Password reset successfully..");
      sessionStorage.removeItem("resetEmail");
      sessionStorage.removeItem("otpVerified");
      navigate("/login");
    } catch (err) {
      setError(err?.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row items-center justify-center bg-white p-4 md:gap-x-14">
      <div className="w-full md:w-1/2 flex justify-center md:justify-end items-center mb-10 md:mb-0 md:pr-4">
        <div className="relative max-w-[500px]">
          <img
            src="/cuate.png"
            alt="Reset Password Illustration"
            className="w-full h-auto"
          />
        </div>
      </div>

      <div className="w-full md:w-1/2 flex justify-center md:justify-start md:pl-4">
        <div className="w-full max-w-[450px] bg-[#f0f9ff] rounded-[32px] p-5 md:p-8 border border-gray-300">
          <div className="mb-5">
            <Link
              to="/verify-otp"
              className="flex items-center text-sm font-semibold text-[#1a2333] hover:underline mb-4"
            >
              <ArrowLeft size={16} className="mr-1" /> Back to Verify OTP
            </Link>
            <h1 className="text-3xl font-bold text-[#1a1a1a] mb-2">
              Reset Password
            </h1>
            <p className="text-gray-500 text-lg">
              Create a strong new password to protect your account.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {error && (
              <p className="text-red-500 text-sm mb-4 bg-red-50 p-2 rounded-lg text-center font-medium">
                {error}
              </p>
            )}

            <div className="relative group mb-6">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500" />
              </div>

              <input
                required
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New Password"
                className="block w-full pl-12 pr-12 py-3 bg-white border rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all border-gray-300"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <div className="relative group mb-6">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500" />
              </div>

              <input
                required
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
                className="block w-full pl-12 pr-12 py-3 bg-white border rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all border-gray-300"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <div className="mb-8 space-y-2">
              <div className="flex items-center text-sm">
                <CheckCircle2 size={16} className={`mr-2 ${newPassword.length >= 8 ? "text-green-500" : "text-gray-300"}`} />
                <span className={newPassword.length >= 8 ? "text-green-600 font-medium" : "text-gray-500"}>At least 8 characters</span>
              </div>
              <div className="flex items-center text-sm">
                <CheckCircle2 size={16} className={`mr-2 ${newPassword && newPassword === confirmPassword ? "text-green-500" : "text-gray-300"}`} />
                <span className={newPassword && newPassword === confirmPassword ? "text-green-600 font-medium" : "text-gray-500"}>Passwords match</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-[#1a2333] hover:bg-[#252f44] text-white font-semibold py-3 rounded-xl transition-all shadow-md ${loading ? "opacity-70" : "active:scale-[0.99]"}`}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          {loading && (
            <div className="loader-col">
              <RiseLoader color="#35e6e9" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
